import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Task16 from '@carbon/icons-react/es/task/16';
import { fromPairs, pipe } from 'ramda';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { EnabledSourcesConfiguration } from 'api/timesheets/configuration/configuration';
import { ReactComponent as Activity24Icon } from 'assets/svgs/activity.svg';
import { ReactComponent as MilestoneIcon } from 'assets/svgs/milestone-timesheet-settings.svg';
import { ReactComponent as PhaseIcon } from 'assets/svgs/phase-timesheet-settings.svg';
import {
  CheckboxGroup,
  Label,
} from 'components/molecules/visibility-options/styled';
import { Icon } from 'components/organisms/document/parts/styled-components';
import { map, orElse } from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useTranslation } from 'hooks/use-translation';
import { ControlPanel } from '../control-panel';
import { SetState, State } from '../state';

const StyledIcon = styled(Icon)`
  color: var(--gray-70);
  svg:first-child {
    width: 18px;
    height: 40px;
    margin-right: var(--spacing-03);
  }
`;

const StyledCheckboxGroup = styled(CheckboxGroup)`
  /* To override the color of label changing to grey when disabled */
  .ant-checkbox-wrapper-disabled > span > div > span {
    color: var(--black-100);
  }
  width: fit-content;
`;

const sourceType = ['TASK', 'PHASE', 'PROJECT', 'ADHOC', 'MILESTONE'];

interface Props {
  configuration: State<EnabledSourcesConfiguration>;
  onConfigurationChange: SetState<EnabledSourcesConfiguration>;
}
const entryTypes = [
  { label: 'Projects', value: 'PROJECT', icon: <Task16 /> },
  { label: 'Phases', value: 'PHASE', icon: <PhaseIcon /> },
  { label: 'Milestones', value: 'MILESTONE', icon: <MilestoneIcon /> },
  { label: 'Tasks', value: 'TASK', icon: <ListChecked16 /> },
  { label: 'Activities', value: 'ADHOC', icon: <Activity24Icon /> },
];

export const EntryTypeSettings: FC<Props> = ({
  configuration,
  onConfigurationChange,
}) => {
  const label = useTranslation('settings.timesheets.controls.entry_type.label');
  const description = useTranslation(
    'settings.timesheets.controls.entry_type.description',
  );

  const shouldConfigBeDisabled = useCallback(
    (val: string) => {
      if (configuration[1] && configuration[1][val]) {
        const temp = Object.keys(configuration[1]).filter((x) => x !== val);

        const isRestConfigFalse = temp.filter(
          (x) => configuration[1]?.[x] === true,
        );

        return isRestConfigFalse.length === 0;
      }

      return false;
    },
    [configuration],
  );

  const options = useMemo(
    () =>
      entryTypes.map((x) => ({
        value: x.value,
        label: (
          <Label data-cy={`timesheet.entry-type.${x.label}`}>
            <StyledIcon>{x.icon}</StyledIcon>
            <span>{x.label}</span>
          </Label>
        ),
        disabled: shouldConfigBeDisabled(x.value),
      })),
    [shouldConfigBeDisabled],
  );

  const value = useMemo(
    () =>
      sourceType.filter((x) =>
        pipe(
          () => configuration,
          maybeValueOf,
          map((config) => config[x]),
          orElse(false),
        )(),
      ),
    [configuration],
  );

  const onChange = useCallback(
    (next) => {
      return onConfigurationChange(
        fromPairs(
          sourceType.map((x) => [x, next.includes(x)]),
        ) as EnabledSourcesConfiguration,
      );
    },
    [onConfigurationChange],
  );

  return (
    <ControlPanel label={label} descriptions={[description]}>
      <span data-cy="timehseet-entry-type-checkbox-group">
        <StyledCheckboxGroup
          options={options}
          value={value}
          onChange={onChange}
        />
      </span>
    </ControlPanel>
  );
};
